import { ClickEvent, CrudDecorator, DataSourceMode, Layout, LookupModelSearchEvent, TableSelectionEvent } from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { AutogenLayoutPartnerProfileManagement } from "./autogen/AutogenLayoutPartnerProfileManagement";
import { AddNewProfileDialog } from "./AddNewProfileDialog";
import { TenderProfile } from "./TenderProfile";
import { BillingProfile } from "./BillingProfile";
import { GenericProfile } from "./GenericProfile";
import { EdiRemitAdvProf } from "./EdiRemitAdvProf";
import { StatusProfile } from "./StatusProfile";
import { ModelEdiTemplate } from "./models/ModelEdiTemplate";
import { ImportSnapshotManager } from "./ImportSnapshotManager";
import { ExportSnapshotManager, LayoutMapping } from "./ExportSnapshotManager";
import { StringUtil } from "@mcleod/core/src/StringUtil";

export class PartnerProfileManagement extends AutogenLayoutPartnerProfileManagement {
  private ALL = 'Z';
  private readonly MAX_DOWNLOAD_RECORDS = 12;
  private tenderDetailLayout = "lme/datafusion/TenderProfile";
  private invoiceDetailLayout = "lme/datafusion/BillingProfile";
  private genericDetailLayout = "lme/datafusion/GenericProfile";
  private remittanceDetailLayout = "lme/datafusion/EdiRemitAdvProf";
  private statusDetailLayout = "lme/datafusion/StatusProfile";
  private importSnapshotManager: ImportSnapshotManager;
  private exportSnapshotManager: ExportSnapshotManager;

  async onLoad() {
    // Initialize the import and export snapshot managers
    this.importSnapshotManager = new ImportSnapshotManager();
    this.exportSnapshotManager = new ExportSnapshotManager();

    const layoutMappings = [
      { profileType: 'ediorder_profile', direction: "I", layout: this.layoutTenderInbound, title: "Tender - Inbound", detailLayout: this.tenderDetailLayout },
      { profileType: 'ediorder_profile', direction: "O", layout: this.layoutTenderOutbound, title: "Tender - Outbound", detailLayout: this.tenderDetailLayout },
      { profileType: 'edibilling_profile', direction: "I", layout: this.layoutInvoiceInbound, title: "Invoice - Inbound", detailLayout: this.invoiceDetailLayout },
      { profileType: 'edibilling_profile', direction: "O", layout: this.layoutInvoiceOutbound, title: "Invoice - Outbound", detailLayout: this.invoiceDetailLayout },
      { profileType: 'edi_profile', direction: "I", layout: this.layoutGenericInbound, title: "Generic - Inbound", detailLayout: this.genericDetailLayout },
      { profileType: 'edi_remit_adv_prof', direction: "I", layout: this.layoutRemittanceInbound, title: "Remittance - Inbound", detailLayout: this.remittanceDetailLayout },
      { profileType: 'edistatus_profile', direction: "I", layout: this.layoutStatusInbound, title: "Shipment Status - Inbound", detailLayout: this.statusDetailLayout },
      { profileType: 'edistatus_profile', direction: "O", layout: this.layoutStatusOutbound, title: "Shipment Status - Outbound", detailLayout: this.statusDetailLayout }
    ]
    layoutMappings.forEach(({ profileType, direction, layout, title, detailLayout }) => {
      layout.setTableValues(direction, profileType, title, detailLayout);
    });

    // Set up selection change handlers for all tables
    this.setupSelectionChangeHandlers();

    await this.mainDataSource.search({});

    // Initialize button state
    this.updateDownloadButtonState();
  }

  /**
   * Gets the layout mappings for export functionality
   */
  private getLayoutMappings(): LayoutMapping[] {
    return [
      { layout: this.layoutTenderInbound?.tableProfiles, transactionType: "T" },
      { layout: this.layoutTenderOutbound?.tableProfiles, transactionType: "T" },
      { layout: this.layoutStatusInbound?.tableProfiles, transactionType: "S" },
      { layout: this.layoutStatusOutbound?.tableProfiles, transactionType: "S" },
      { layout: this.layoutInvoiceInbound?.tableProfiles, transactionType: "I" },
      { layout: this.layoutInvoiceOutbound?.tableProfiles, transactionType: "I" },
      { layout: this.layoutRemittanceInbound?.tableProfiles, transactionType: "A" },
      { layout: this.layoutGenericInbound?.tableProfiles, transactionType: "Z" }
    ];
  }

  commBeforeLookupModelSearch(event: LookupModelSearchEvent) {
    event.filter.type = this.ALL;
  }

  onClickExpandAllButton() {
    this.onClickExpandCollapse(true);
  }

  onClickCollapseAllButton() {
    this.onClickExpandCollapse(false);
  }

  onClickExpandCollapse(isExpanding: boolean) {
    const layouts = [
      this.layoutTenderInbound,
      this.layoutStatusOutbound,
      this.layoutInvoiceOutbound,
      this.layoutGenericInbound,
      this.layoutRemittanceInbound,
      this.layoutTenderOutbound,
      this.layoutStatusInbound,
      this.layoutInvoiceInbound
    ];
    layouts.forEach(async layout => isExpanding ? await layout?.expandPanel() : await layout?.collapsePanel());
  }

  async addNewProfileOnClick(event: any) {
    const dialog: AddNewProfileDialog = Layout.getLayout("lme/datafusion/AddNewProfileDialog") as AddNewProfileDialog;
    const result = await CommonDialogs.showDialog(dialog, { title: "Add New Profile" });
    if (!result.wasCancelled) {
      const transactionType = dialog.textboxTransactionType.selectedItem?.value;
      const direction = dialog.textboxDirection.selectedItem?.value;
      let templateId = null;
      let templateName = null;
      if (dialog.textboxTemplate.lookupModelData?.length > 0) {
        templateId = dialog.textboxTemplate.getFirstLookupModelData()?.get("id");
        templateName = dialog.textboxTemplate.getFirstLookupModelData()?.get("name");
      }
      const layout = this.getLayoutByTransactionType(transactionType);

      const crudDecorator = new CrudDecorator({
        layout: layout,
        mode: DataSourceMode.ADD,
        headerProps: {
          showClose: true,
          showSaveAndClose: true
        },
        layoutLoadListeners: async () => {
          // Set up the DataSource in ADD mode with a blank row first
          await layout.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await layout.mainDataSource.createBlankRow()]);
          if (!StringUtil.isEmptyString(templateId)) {
            const template = await new ModelEdiTemplate().searchSingle({ id: templateId });
            if (template) {
              if (direction === "I") {
                layout.mainDataSource.activeRow.set("partner_id", template.get("app_sender_id"));
                layout.mainDataSource.activeRow.set("alt_partner_id", template.get("sender_id"));
              } else {
                layout.mainDataSource.activeRow.set("partner_id", template.get("app_receiver_id"));
                layout.mainDataSource.activeRow.set("alt_partner_id", template.get("receiver_id"));
              }
              layout.mainDataSource.activeRow.set("transaction_set", template.get("transaction_set"));
              layout.mainDataSource.activeRow.set("version", template.get("version"));
            }
            layout.mainDataSource.activeRow.setValueAndLookupModelData("template_id", templateId, { id: templateId, name: templateName });
          }
          layout.mainDataSource.activeRow.set("direction", direction);
          if (layout instanceof TenderProfile && direction === "I") {
            layout.mainDataSource.activeRow.set("prompt_xref_edi_loc", "N");
          } else if (layout instanceof BillingProfile) {
            layout.mainDataSource.activeRow.set("audit_interval_hours", 2);
            layout.mainDataSource.activeRow.set("audit_interval_minutes", 0);
          }
          await layout.afterExecution(null);
          layout.setTitle();
        },
        doAfterSlideOut: async () => {
          const layouts = [
            this.layoutTenderInbound,
            this.layoutStatusOutbound,
            this.layoutInvoiceOutbound,
            this.layoutGenericInbound,
            this.layoutRemittanceInbound,
            this.layoutTenderOutbound,
            this.layoutStatusInbound,
            this.layoutInvoiceInbound
          ];
          layouts.forEach(layout => layout.initialLoad = true);
          await this.mainDataSource.search({});
        }
      });

      crudDecorator.slideIn({ speed: 200 });
    }
  }

  private getLayoutByTransactionType(transactionType: string) {
    switch (transactionType) {
      case 'T':
        return Layout.getLayout(this.tenderDetailLayout) as TenderProfile;
      case 'I':
        return Layout.getLayout(this.invoiceDetailLayout) as BillingProfile;
      case 'G':
        return Layout.getLayout(this.genericDetailLayout) as GenericProfile;
      case 'A':
        return Layout.getLayout(this.remittanceDetailLayout) as EdiRemitAdvProf;
      case 'S':
        return Layout.getLayout(this.statusDetailLayout) as StatusProfile;
      default:
        return null;
    }
  }


  /** This is an event handler for the onClick event of buttonExportSnap. */
  buttonImportSnapOnClick(event: ClickEvent) {
    // Create file input element for .zip file selection
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.zip';
    fileInput.style.display = 'none';

    // Add event listener for file selection
    fileInput.addEventListener('change', (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];

      if (file) {
        if (file.type === 'application/zip' || file.name.toLowerCase().endsWith('.zip')) {
          // Handle the selected zip file using the ImportSnapshotManager and capture return values
          this.importSnapshotManager.handleZipFileUpload(file, (parentRecordTableName, parentRecordId) => {
            // This callback will be called after the import process completes
            this.openProfile(parentRecordTableName, parentRecordId);
          }).then((result) => {
            const parentRecordTableName = result.parentRecordTableName;
            const parentRecordId = result.parentRecordId;
            console.log('Captured parent record info:', {
              parentRecordTableName,
              parentRecordId
            });
          }).catch((error) => {
            console.error('Error handling zip file upload:', error);
          });
        } else {
          alert('Please select a valid .zip file');
        }
      }

      // Clean up the file input element
      document.body.removeChild(fileInput);
    });

    // Add to DOM and trigger click
    document.body.appendChild(fileInput);
    fileInput.click();
  }

  /**
   * Sets up selection change handlers for all tables to monitor total selected records
   */
  private setupSelectionChangeHandlers() {
    const allTables = this.getAllTables();
    this.exportSnapshotManager.setupSelectionChangeHandlers(allTables, () => {
      this.updateDownloadButtonState();
    });
  }

  /**
   * Gets all tables from all tab layouts
   */
  private getAllTables() {
    return this.exportSnapshotManager.getAllTables([
      this.layoutTenderInbound?.tableProfiles,
      this.layoutTenderOutbound?.tableProfiles,
      this.layoutStatusInbound?.tableProfiles,
      this.layoutStatusOutbound?.tableProfiles,
      this.layoutInvoiceInbound?.tableProfiles,
      this.layoutInvoiceOutbound?.tableProfiles,
      this.layoutGenericInbound?.tableProfiles,
      this.layoutRemittanceInbound?.tableProfiles
    ]);
  }

  /**
   * Updates the download button state based on total selected records
   */
  private updateDownloadButtonState() {
    const allTables = this.getAllTables();
    this.exportSnapshotManager.updateDownloadButtonState(this.buttonDownloadSnap, allTables);
  }

  /** This is an event handler for the onClick event of buttonDownloadSnap. */
  async buttonDownloadSnapOnClick(event: ClickEvent) {
    const layoutMappings = this.getLayoutMappings();
    await this.exportSnapshotManager.handleDownloadButtonClick(event, layoutMappings);
  }

  /**
   * Opens the appropriate profile in a CRUD decorator based on the table name and profile ID
   */
  private openProfile(parentRecordTableName: string, parentRecordId: string): void {
    // Map table names to detail layouts using the same logic from onLoad
    const tableToLayoutMap: { [key: string]: string } = {
      'ediorder_profile': 'lme/datafusion/TenderProfile',
      'edibilling_profile': 'lme/datafusion/BillingProfile',
      'edi_profile': 'lme/datafusion/GenericProfile',
      'edi_remit_adv_prof': 'lme/datafusion/EdiRemitAdvProf',
      'edistatus_profile': 'lme/datafusion/StatusProfile'
    };

    const detailLayout = tableToLayoutMap[parentRecordTableName];

    if (!detailLayout) {
      console.warn(`No layout mapping found for table: ${parentRecordTableName}`);
      CommonDialogs.showMessage(`Unknown profile type: ${parentRecordTableName}`);
      return;
    }

    try {
      const profileLayout = Layout.getLayout(detailLayout) as any;

      const crudDecorator = new CrudDecorator({
        layout: profileLayout,
        headerProps: {
          showClose: true,
          showSaveAndClose: true
        },
        layoutLoadListeners: async () => {
          if (profileLayout.mainDataSource) {
            await profileLayout.mainDataSource.search({ id: parentRecordId });
            if (profileLayout.mainDataSource.data.length > 0) {
              await profileLayout.afterExecution(null);
              profileLayout.setTitle();
            } else {
              console.warn(`No profile found with ID: ${parentRecordId}`);
            }
          }
        },
        doAfterSlideOut: () => {
          this.mainDataSource.search({});
        }
      });

      crudDecorator.slideIn({ speed: 200 });

    } catch (error) {
      console.error('Error opening profile:', error);
      CommonDialogs.showError(`Failed to open profile: ${String(error)}`);
    }
  }
}
